'use client';

import { useRouter } from 'next/navigation';
import { useCallback } from 'react';
import { ArrowLeft } from 'lucide-react';
import { cn } from '@/core/utils/cn';
import { useTranslation } from '@/core/i18n';

interface PageHeaderProps {
  id?: string;
  title: string;
  description?: string;
  actions?: React.ReactNode;
  className?: string;
  /** @deprecated — kept for API compat; back button now uses browser history */
  backHref?: string;
  /** Show a back button that navigates using browser history */
  showBack?: boolean;
}

export function PageHeader({ id, title, description, actions, className, backHref, showBack }: PageHeaderProps) {
  const router = useRouter();
  const hasBack = showBack || !!backHref;
  const { t } = useTranslation();

  const handleBack = useCallback(() => {
    if (window.history.length > 1) {
      router.back();
    } else {
      router.push(backHref || '/');
    }
  }, [router, backHref]);

  return (
    <div id={id} className={cn('flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3', className)}>
      <div className="min-w-0 flex items-start gap-3">
        {hasBack && (
          <button
            id={id ? `${id}-back-btn` : 'page-header-back-btn'}
            type="button"
            onClick={handleBack}
            className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-hms-border bg-hms-surface text-hms-textSub hover:text-hms-text hover:border-hms-gold/30 transition-colors"
            aria-label={t('shared.pageHeader.goBack')}
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
        )}
        <div className="min-w-0">
          <h1 className="text-lg sm:text-xl font-semibold text-hms-text tracking-tight">{title}</h1>
          {description && (
            <p className="text-[13px] text-hms-textSub mt-1">{description}</p>
          )}
        </div>
      </div>
      {actions && <div className="flex items-center gap-2 flex-shrink-0">{actions}</div>}
    </div>
  );
}
